import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../shared/utils/api';
import { confColor, setThresholds } from './confColorUtils';
import SubjectGroup from './TopicGroup';
import { subjectSlug } from '../../shared/utils/subjectDisplay';
import { DEFAULT_STATUSES } from './TopicDetail';

export default function TopicsPage({ module, focusTopicId, onFocusConsumed, tourHighlight, onNavigateToSRA }) {
  const [topics, setTopics] = useState([]);
  const [studyUnits, setStudyUnits] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState(null);
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [sraTopicStats, setSraTopicStats] = useState({});
  const [thRed, setThRed] = useState(0.4);
  const [thGreen, setThGreen] = useState(0.7);
  const [plannerDetailLevel, setPlannerDetailLevel] = useState('study_units');

  const [searchParams] = useSearchParams();
  const urlFocusId = parseInt(searchParams.get('focus'), 10) || null;
  const effectiveFocusId = focusTopicId || urlFocusId;

  useEffect(() => {
    api('/api/settings').then(s => {
      try {
        const custom = JSON.parse(s.custom_statuses || '[]');
        setStatuses(custom.length > 0 ? custom : DEFAULT_STATUSES);
      } catch { setStatuses(DEFAULT_STATUSES); }
      let newRed = thRed, newGreen = thGreen;
      if (s.confidence_threshold_red) { newRed = parseFloat(s.confidence_threshold_red); setThRed(newRed); }
      if (s.confidence_threshold_green) { newGreen = parseFloat(s.confidence_threshold_green); setThGreen(newGreen); }
      setThresholds(newRed, newGreen);
      if (s.planner_detail_level) setPlannerDetailLevel(s.planner_detail_level);
    }).catch((err) => { console.error('[Topics] Failed to load settings:', err); });
  }, []);

  useEffect(() => {
    api('/api/sra/topic-stats').then(data => {
      if (data && typeof data === 'object' && !data.msg) setSraTopicStats(data);
    }).catch((err) => { console.error('[Topics] Failed to load SRA topic stats:', err); });
  }, []);

  useEffect(() => {
    api('/api/study-units').then(setStudyUnits).catch(() => setStudyUnits([]));
  }, []);

  const moduleVariant = module === 'FLK1' ? 'flk1' : 'flk2';
  const moduleColor = module === 'FLK1' ? 'var(--flk1)' : 'var(--flk2)';
  const effectiveDetailLevel = plannerDetailLevel === 'L2'
    ? 'study_units'
    : (plannerDetailLevel || 'study_units');

  useEffect(()=>{
    setLoading(true); setTopics([]); setFilter('all'); setSearch('');
    api(`/api/topics/${module}`).then(data=>{setTopics(data);setLoading(false);});
  },[module]);

  useEffect(()=>{
    if(effectiveFocusId&&!loading){
      const timer=setTimeout(()=>onFocusConsumed&&onFocusConsumed(),1500);
      return()=>clearTimeout(timer);
    }
  },[effectiveFocusId,loading,onFocusConsumed]);

  const handleSave=useCallback((id,updates)=>{
    setTopics(prev=>prev.map(t=>t.id===id?{...t,...updates}:t));
  },[]);

  const handleTopicsBulkUpdate = useCallback((topicIds, updatedSubset) => {
    const updatedById = new Map(updatedSubset.map(t => [t.id, t]));
    setTopics(prev => prev.map(t => updatedById.get(t.id) || t));
  }, []);

  const handleRateAll = useCallback(async (generalTopic, childTopics, confidence) => {
    // Optimistic update
    setTopics(prev => prev.map(t =>
      t.general_topic === generalTopic
        ? { ...t, confidence, is_estimated: true }
        : t
    ));
    await api('/api/topics/cascade-by-subject', {
      method: 'POST',
      body: JSON.stringify({ level: 'L2', general_topic: generalTopic, module, confidence }),
    });
  }, [module]);

  // Search filter (L3 level — determines which L3 topics are visible)
  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return topics;
    return topics.filter(t =>
      (t.subtopic || '').toLowerCase().includes(q) ||
      (t.display_name || '').toLowerCase().includes(q) ||
      (t.main_topic || t.topic || '').toLowerCase().includes(q) ||
      (t.general_topic || '').toLowerCase().includes(q) ||
      (t.sub_subtopic || t.detail || '').toLowerCase().includes(q)
    );
  }, [topics, search]);

  // Compute L2 groups from full unfiltered topics (for filter pill counts)
  const allL2Groups = useMemo(() => {
    const groups = {};
    topics.forEach(t => {
      const key = t.general_topic || 'General';
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return Object.entries(groups).map(([name, children]) => {
      const hasAny = children.some(t => t.confidence !== null);
      const avg = hasAny
        ? children.reduce((s, t) => s + (t.confidence || 0), 0) / children.length
        : null;
      return { name, children, avg, hasAny };
    });
  }, [topics]);

  // Classify a value into one of the four filter bands using the user's thresholds.
  //   null → 'untouched'; < thRed → 'weak'; < thGreen → 'moderate' (no filter); else 'strong'
  const bandOf = useCallback((value) => {
    if (value === null || value === undefined) return 'untouched';
    if (value < thRed) return 'weak';
    if (value < thGreen) return 'moderate';
    return 'strong';
  }, [thRed, thGreen]);

  // Aggregate a study unit's confidence using the same equal-weight formula
  // as the aggregateStudyUnit util, but inline to avoid circular imports.
  const studyUnitConf = useCallback((memberIds) => {
    const memberSet = new Set(memberIds);
    const members = topics.filter(t => memberSet.has(t.id));
    const vals = members.map(t => t.raw_confidence ?? t.confidence).filter(v => v != null);
    if (vals.length === 0) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [topics]);

  // Counts at display level: study units if mode = 'study_units', else L3 topics.
  // "Strong 20" now means "20 study units with aggregated confidence >= thGreen"
  // in study_units mode, not "topics inside L2s whose average is strong".
  const displayStudyUnitsForModule = useMemo(
    () => (studyUnits || []).filter(su => su.module === module),
    [studyUnits, module]
  );

  const counts = useMemo(() => {
    if (effectiveDetailLevel === 'study_units' && studyUnits) {
      const bandCounts = { all: 0, weak: 0, untouched: 0, strong: 0, moderate: 0 };
      displayStudyUnitsForModule.forEach(su => {
        const conf = studyUnitConf(su.member_topic_ids);
        const band = bandOf(conf);
        bandCounts.all += 1;
        bandCounts[band] += 1;
      });
      return bandCounts;
    }
    // L3 mode — count individual topics
    return {
      all: topics.length,
      weak: topics.filter(t => bandOf(t.raw_confidence ?? t.confidence) === 'weak').length,
      untouched: topics.filter(t => bandOf(t.raw_confidence ?? t.confidence) === 'untouched').length,
      strong: topics.filter(t => bandOf(t.raw_confidence ?? t.confidence) === 'strong').length,
      moderate: topics.filter(t => bandOf(t.raw_confidence ?? t.confidence) === 'moderate').length,
    };
  }, [effectiveDetailLevel, studyUnits, displayStudyUnitsForModule, studyUnitConf, topics, bandOf]);

  // Filter the items-to-render based on the active filter pill.
  // In study_units mode: filter study units AND filter topics to just those
  // that belong to passing study units. This means L3 members of passing
  // study units all render (grouping is preserved), but other study units
  // disappear.
  // In L3 mode: filter topics directly — only L3s matching the band render,
  // regardless of their L2 / study unit container.
  const filtered = useMemo(() => {
    if (filter === 'all') return searchFiltered;
    if (effectiveDetailLevel === 'study_units' && studyUnits) {
      // Find passing study unit IDs, then emit only their member topics
      const passingMemberIds = new Set();
      displayStudyUnitsForModule.forEach(su => {
        const conf = studyUnitConf(su.member_topic_ids);
        if (bandOf(conf) === filter) {
          su.member_topic_ids.forEach(id => passingMemberIds.add(id));
        }
      });
      return searchFiltered.filter(t => passingMemberIds.has(t.id));
    }
    // L3 mode — filter topics directly by their own band
    return searchFiltered.filter(t => bandOf(t.raw_confidence ?? t.confidence) === filter);
  }, [filter, searchFiltered, effectiveDetailLevel, studyUnits, displayStudyUnitsForModule, studyUnitConf, bandOf]);

  // Group by main_topic (uses new column if present, falls back to topic field)
  const grouped = {};
  const subjectOrder = [];
  filtered.forEach(t => {
    const key = t.main_topic || t.topic || 'Other';
    if (!grouped[key]) { grouped[key] = []; subjectOrder.push(key); }
    grouped[key].push(t);
  });
  const uniqueSubjects = [...new Set(subjectOrder)];

  const flk1Desc = 'Business Law · Dispute Resolution · Contract Law · Tort · Legal System · Constitutional & Administrative Law · Legal Services · Ethics';
  const flk2Desc = 'Property Law & Practice · Wills & Administration · Solicitors Accounts · Land Law · Trusts Law · Criminal Liability · Criminal Practice · Ethics';

  if(loading) return <div className="loading">Loading {module}</div>;

  return (
    <div className="page-container">
    <div className="topics-page">
      <h1 className="page-heading">{module} topics</h1>

      {/* L1 subject-area pills — smooth-scroll to section on click.
          The old module-header-bar (with the dot-separated subject list)
          was removed — the pills below are the canonical module nav. */}
      <div className="nav-pills">
        <span className="nav-pill nav-pill-active">{module}</span>
        {uniqueSubjects.map(s => (
          <span
            key={s}
            className="nav-pill"
            onClick={() => {
              const el = document.getElementById(`subject-${subjectSlug(s)}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {s}
          </span>
        ))}
      </div>

      <div className="topics-toolbar">
        <input className="search-input" placeholder="Search topics, areas, keywords" value={search} onChange={e=>setSearch(e.target.value)}/>
      </div>

      <div className="filter-row">
        {[
          ['all','All'],
          ['weak','Needs work'],
          ['untouched','Not started'],
          ['strong','Strong'],
        ].map(([k,label])=>(
          <button key={k} className={`filter-btn filter-btn-${k} ${filter===k?'active':''}`}
            onClick={()=>setFilter(k)}>
            {label} <span className="filter-count">{counts[k]}</span>
          </button>
        ))}
      </div>

      {filtered.length===0&&<div className="empty">No topics match this filter</div>}

      {uniqueSubjects.map((subject, i) => (
        <SubjectGroup
          key={subject}
          subject={subject}
          topics={grouped[subject]}
          onSave={handleSave}
          forceOpen={groupsOpen}
          moduleColor={moduleColor}
          focusTopicId={effectiveFocusId}
          statuses={statuses}
          highlightFirstTopic={tourHighlight && i === 0}
          sraStat={sraTopicStats[subject] || null}
          sraTopicStats={sraTopicStats}
          onRateAll={handleRateAll}
          autoExpandL2={!!search}
          onNavigateToSRA={onNavigateToSRA}
          detailMode={effectiveDetailLevel}
          studyUnits={studyUnits ? studyUnits.filter(su => su.module === module) : null}
          onTopicsBulkUpdate={handleTopicsBulkUpdate}
        />
      ))}
    </div>
    </div>
  );
}
